/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo } from "react";
import { Alert } from "@mui/material";
import { InboxOutlined } from "@ant-design/icons";
import { Upload } from "antd";
import DownloadIcon from "@mui/icons-material/Download";
import {
  FieldChip,
  FieldChipRow,
  FieldsSection,
  FieldsSectionTitle,
  FileNameText,
  RequiredNote,
  TemplateButton,
  UploadDropZone,
  UploadHelperCard,
  UploadHelperSubtitle,
  UploadHelperTextBlock,
  UploadHelperTitle,
  UploadIconContainer,
  UploadInstructionAccent,
  UploadInstructionText,
  UploadPromptContainer,
  UploadSubText,
} from "./styles.jsx";

const FIELD_CHIPS = [
  { label: "First Name*", required: true },
  { label: "Last Name*", required: true },
  { label: "Phone*", required: true },
  { label: "Job Title*", required: true },
  { label: "Email", required: false },
  { label: "Date of Birth", required: false },
  { label: "Wage", required: false },
];

export default function UploadStep({ selectedFile, onSelectFile, onClearFile, uploadError, onDownloadTemplate }) {
  const { Dragger } = Upload;

  const uploadProps = useMemo(() => ({
    accept: ".csv,text/csv",
    multiple: false,
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      onSelectFile(file);
      return false;
    },
    onRemove: () => {
      onClearFile();
    },
  }), [onClearFile, onSelectFile]);

  return (
    <div data-testid="manual-csv-import-upload-step">
      {uploadError ? (
        <Alert data-testid="manual-csv-import-upload-error" severity="warning" sx={{ marginBottom: "16px", borderRadius: "12px" }}>
          {uploadError}
        </Alert>
      ) : null}

      <Dragger {...uploadProps} style={{ border: 0, background: "transparent", padding: 0 }}>
        <UploadDropZone>
          <UploadPromptContainer>
            <UploadIconContainer>
              <InboxOutlined />
            </UploadIconContainer>
            <UploadInstructionText>
              Drop your CSV file here, or <UploadInstructionAccent>click to browse</UploadInstructionAccent>
            </UploadInstructionText>
            <UploadSubText>
              Up to 5MB .csv format
            </UploadSubText>
            {selectedFile ? <FileNameText>{selectedFile.name}</FileNameText> : null}
          </UploadPromptContainer>
        </UploadDropZone>
      </Dragger>

      <UploadHelperCard>
        <UploadHelperTextBlock>
          <UploadHelperTitle>
            Not sure about the format?
          </UploadHelperTitle>
          <UploadHelperSubtitle>
            Download a template with the expected columns
          </UploadHelperSubtitle>
        </UploadHelperTextBlock>

        <TemplateButton data-testid="manual-csv-import-template-download" variant="outlined" startIcon={<DownloadIcon />} onClick={onDownloadTemplate}>
          Template
        </TemplateButton>
      </UploadHelperCard>

      <FieldsSection>
        <FieldsSectionTitle>
          Fields the system will import:
        </FieldsSectionTitle>
        <FieldChipRow>
          {FIELD_CHIPS.map((field) => (
            <FieldChip key={field.label} label={field.label} requiredfield={field.required ? 1 : 0} />
          ))}
        </FieldChipRow>
        <RequiredNote>
          * required - other columns in your file are ignored
        </RequiredNote>
      </FieldsSection>
    </div>
  );
}
